import { useUser } from '@adsk/alloy-provider';
import Theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';
import styled from 'styled-components';
import Avatar from '@adsk/alloy-react-avatar';
import { InlineLoader, InlineLoaderProps, LoaderStyle } from '../InlineLoader';
import { useOxygen } from '../../providers/OxygenProvider';
import { Assignee } from '../../sdk';
import { useSortAssignees } from './hooks';
import { CellItem, ToolTipContent } from './components';
import { getAssigneeDisplayName, getAssigneeCompanyName, translate } from '../../helpers';
import AssigneeAvatar from '../AssigneeAvatar';

const AVATAR_STYLE = { marginRight: '16px', alignSelf: 'center' };

export enum VARIANTS {
  TABLE_CELL = 'TABLE_CELL',
  SINGLE_USER = 'SINGLE_USER',
  SINGLE_LINE = 'SINGLE_LINE',
}

type AssigneeCellVariants = typeof VARIANTS;
type AssigneeCellVariant = AssigneeCellVariants[keyof AssigneeCellVariants];

const AssigneeCellWrapper = styled.span({
  display: 'flex',
  width: '100%',
  ...Theme.typography.bodyMedium,
});

const Plus1Wrapper = styled.span({
  color: Theme.colors.charcoal700,
});

const getTooltipTargetProps = () => ({
  style: { maxWidth: '100%', display: 'flex', alignItems: 'center' },
});

const CellContentWrapper = styled.div({ width: '100%', ...Theme.typography.ellipsis });

export type AssigneeCellProps = {
  assignees?: Assignee[];
  showAvatar?: boolean;
  variant?: AssigneeCellVariant;
  className?: string;
  loaderProps?: InlineLoaderProps;
  highlightCurrentUser?: boolean;
  isTargetRfi?: boolean;
};

export const AssigneeCell = ({
  assignees: unsortedAssignees,
  showAvatar,
  variant = VARIANTS.TABLE_CELL,
  className,
  loaderProps,
  highlightCurrentUser = true,
  isTargetRfi = false,
}: AssigneeCellProps) => {
  const { user: currentUser } = useUser();
  const oxygen = useOxygen();
  const assignees = useSortAssignees(unsortedAssignees, isTargetRfi);

  if (!assignees?.length) {
    return <div data-testid="assignee-cell">-</div>;
  }

  if (oxygen.loading) {
    return <InlineLoader loaderStyle={LoaderStyle.Text} {...loaderProps} />;
  }

  /* Handling 1st user */
  const autodeskId = assignees[0].id;
  const userName = getAssigneeDisplayName({ oxygen, assignee: assignees[0], isTargetRfi });
  const userCompanyName = getAssigneeCompanyName({ oxygen, assignee: assignees[0], isTargetRfi });
  const shouldShowPlusMore = assignees.length > 1 && variant !== VARIANTS.SINGLE_USER;
  const plusMoreText = translate('plusXMore', { usersCount: assignees.length - 1 });
  const isPlusMoreSingleLine = variant === VARIANTS.SINGLE_LINE;
  const currentUserIsAssignee = currentUser?.autodeskId === autodeskId;
  const shouldHighlightUser = highlightCurrentUser && currentUserIsAssignee;
  const isNameCompanySingleLine =
    variant === VARIANTS.SINGLE_LINE || variant === VARIANTS.TABLE_CELL;

  const cellContent = (
    <CellContentWrapper data-testid="assignee-cell">
      <CellItem
        key="assignee"
        css="font-size: inherit; font-family: inherit; font-weight: inherit;"
      >
        <span
          style={{
            backgroundColor: shouldHighlightUser ? Theme.colors.adskBlue100 : 'inherit',
          }}
        >
          {userName}
          {isNameCompanySingleLine && userCompanyName && ` (${userCompanyName})`}
        </span>
        {shouldShowPlusMore && isPlusMoreSingleLine && (
          <Plus1Wrapper>{` ${plusMoreText}`}</Plus1Wrapper>
        )}
      </CellItem>
      {userCompanyName && !isNameCompanySingleLine && (
        <CellItem key="company" secondary>
          {`(${userCompanyName})`}
        </CellItem>
      )}
      {shouldShowPlusMore && !isPlusMoreSingleLine && (
        <CellItem key="plusMore" secondary>
          {plusMoreText}
        </CellItem>
      )}
    </CellContentWrapper>
  );

  return (
    <AssigneeCellWrapper className={className}>
      <Tooltip
        content={<ToolTipContent assignees={assignees} isTargetRfi={isTargetRfi} />}
        getTargetProps={getTooltipTargetProps}
      >
        {showAvatar && (
          <AssigneeAvatar
            assignee={assignees[0]}
            style={AVATAR_STYLE}
            size={Avatar.SIZES.MEDIUM_24}
            isTargetRfi={isTargetRfi}
          />
        )}
        {cellContent}
      </Tooltip>
    </AssigneeCellWrapper>
  );
};

AssigneeCell.VARIANTS = VARIANTS;
